import React, { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, CalendarDays, CheckSquare, ClipboardList, FileText, Image as ImageIcon, Truck, User, Warehouse } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from './Spinner.js';

const collectionName = 'InwardsLog';

const InwardsLogDetail = ({ db }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    const fetchEntry = async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, collectionName, id));
        if (!snap.exists()) {
          setEntry(null);
          return;
        }
        setEntry({ id: snap.id, ...snap.data() });
      } catch (error) {
        console.error('Error loading Inwards Log detail:', error);
        setEntry(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEntry();
  }, [db, id]);

  const checklistRows = useMemo(() => ([
    ['informedHo', 'INFORMED TO HO FOR MISSING AND DAMAGES (WHATSAPP / SLACK)'],
    ['paperShared', 'PAPER SHARED TO OFFICE (WHATSAPP / SLACK)'],
    ['damagePartsSent', 'DAMAGE PARTS SENT FOR MAINTENANCE (IF ANY)'],
    ['invoiceSent', 'INVOICE / PAPERS SENT TO ACCOUNTS']
  ]), []);

  if (loading) return <Spinner />;

  if (!entry) {
    return (
      <div style={pageStyle} className="inwards-log-detail-page">
        <style>{responsiveStyles}</style>
        <button onClick={() => navigate('/ops/inwards-log')} style={backButtonStyle} type="button">
          <ArrowLeft size={16} />
          Back to Log
        </button>
        <div style={notFoundCardStyle} className="inwards-log-detail-notfound">
          <h2 style={{ marginTop: 0 }}>Inwards Log record not found</h2>
          <p style={{ color: '#64748b', marginBottom: 0 }}>The record may have been removed or the link is invalid.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle} className="inwards-log-detail-page">
      <style>{responsiveStyles}</style>
      <div style={headerRowStyle} className="inwards-log-detail-header">
        <div>
          <h1 style={titleStyle}>{entry.serialNoMic || 'Inwards Log Record'}</h1>
          <p style={descriptionStyle}>
            Submitted on {formatDate(entry.createdAt || entry.dateOfPickup)} for {entry.customerSupplierName || '---'}.
          </p>
        </div>
        <button onClick={() => navigate('/ops/inwards-log')} style={backButtonStyle} type="button">
          <ArrowLeft size={16} />
          Back to Log
        </button>
      </div>

      <div style={heroCardStyle} className="inwards-log-detail-hero">
        <div style={heroMetaGridStyle} className="inwards-log-detail-hero-grid">
          <MetaPill icon={CalendarDays} label="Date of Pickup" value={formatDate(entry.dateOfPickup)} />
          <MetaPill icon={ClipboardList} label="MIC No." value={entry.serialNoMic || '---'} />
          <MetaPill icon={ClipboardList} label="Pickup Challan No." value={entry.serialNoPickupChallan || '---'} />
          <MetaPill icon={Warehouse} label="Warehouse" value={entry.warehouse || '---'} />
        </div>
      </div>

      <div style={sectionGridStyle} className="inwards-log-detail-section-grid">
        <Section title="Basic Details" icon={ClipboardList}>
          <InfoRow label="Organisation" value={entry.organisation} />
          <InfoRow label="Pickup Type" value={entry.pickupType} />
          <InfoRow label="Vehicle Type" value={entry.vehicleType} />
          <InfoRow label="Transport" value={entry.transport} />
          <InfoRow label="Vehicle Number" value={entry.vehicleNumber} />
          <InfoRow label="Weight (kgs)" value={entry.weightKgs} />
        </Section>

        <Section title="Site And Project Details" icon={User}>
          <InfoRow label="Customer / Supplier Name" value={entry.customerSupplierName} />
          <InfoRow label="Project Name" value={entry.projectName} />
          <InfoRow label="Email - Client office" value={entry.emailClientOffice} />
          <InfoRow label="Email - Project office" value={entry.emailProjectOffice} />
          <InfoRow label="Missing / Damages" value={entry.missingDamages} />
        </Section>
      </div>

      <div style={sectionGridStyle} className="inwards-log-detail-section-grid">
        <Section title="Transport Details" icon={Truck}>
          <InfoRow label="Pickup Agent" value={entry.pickupAgent} />
          <InfoRow label="Verified By" value={entry.verifiedBy} />
        </Section>

        <Section title="Checklist" icon={CheckSquare}>
          <div style={{ display: 'grid', gap: '10px' }}>
            {checklistRows.map(([key, label]) => (
              <ChecklistRow key={key} label={label} checked={Boolean(entry.checklists?.[key])} />
            ))}
          </div>
        </Section>
      </div>

      <Section title="Documents" icon={ImageIcon}>
        <FileGroup label="Vehicle image proof" files={entry.vehicleImages} />
        <FileGroup label="Upload MIC" files={entry.micFiles} />
        <FileGroup label="Upload PC / Inwards Challan" files={entry.pcFiles} />
        {entry.missingDamages === 'Yes' && (
          <FileGroup label="Missing / Damage images" files={entry.missingDamageImages} />
        )}
      </Section>

      <Section title="Audit" icon={ClipboardList}>
        <InfoRow label="Record ID" value={entry.id} />
        <InfoRow label="Created At" value={formatDate(entry.createdAt)} />
        <InfoRow label="Updated At" value={formatDate(entry.updatedAt)} />
      </Section>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <section style={cardStyle} className="inwards-log-detail-card">
    <div style={sectionTitleRowStyle}>
      <div style={sectionTitleLeftStyle}>
        <Icon size={18} color="#0f766e" />
        <h2 style={sectionTitleStyle}>{title}</h2>
      </div>
    </div>
    {children}
  </section>
);

const MetaPill = ({ icon: Icon, label, value }) => (
  <div style={metaPillStyle} className="inwards-log-detail-meta-pill">
    <Icon size={16} color="#0f766e" />
    <div>
      <div style={metaLabelStyle}>{label}</div>
      <div style={metaValueStyle}>{value || '---'}</div>
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={infoRowStyle} className="inwards-log-detail-info-row">
    <div style={infoLabelStyle}>{label}</div>
    <div style={infoValueStyle}>{value || '---'}</div>
  </div>
);

const ChecklistRow = ({ label, checked }) => (
  <div style={checklistRowStyle} className="inwards-log-detail-checklist-row">
    <span style={checklistBadgeStyle(checked)}>{checked ? 'Yes' : 'No'}</span>
    <span style={checklistLabelStyle}>{label}</span>
  </div>
);

const FileGroup = ({ label, files }) => (
  <div style={{ marginBottom: '16px' }}>
    <div style={fileGroupLabelStyle}>{label}</div>
    {renderLocalFiles(files)}
  </div>
);

const renderLocalFiles = (files = []) => {
  if (!Array.isArray(files) || files.length === 0) {
    return <div style={emptyStateStyle}>No files uploaded.</div>;
  }

  return (
    <div style={fileGridStyle}>
      {files.map((file, index) => (
        <a key={`${file.url || file.name || 'file'}_${index}`} href={file.url} target="_blank" rel="noreferrer" style={fileCardStyle} className="inwards-log-detail-file-card">
          <FileText size={18} color="#0f766e" />
          <div style={{ minWidth: 0 }}>
            <div style={fileNameStyle} className="inwards-log-detail-file-name">{file.name || 'Attachment'}</div>
            <div style={fileMetaStyle}>{file.type || 'File'}</div>
          </div>
        </a>
      ))}
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '---';
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---';
  return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
};

const pageStyle = {
  maxWidth: '1220px',
  margin: '0 auto',
  padding: '20px 0'
};

const headerRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  marginBottom: '18px'
};

const titleStyle = {
  margin: 0,
  fontSize: '30px',
  lineHeight: 1.15,
  color: '#0f172a'
};

const descriptionStyle = {
  margin: '10px 0 0 0',
  maxWidth: '760px',
  color: '#475569',
  lineHeight: 1.65
};

const backButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  border: '1px solid #cbd5e1',
  borderRadius: '10px',
  padding: '10px 14px',
  backgroundColor: 'white',
  color: '#0f172a',
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap'
};

const heroCardStyle = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  padding: '18px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
  marginBottom: '16px'
};

const heroMetaGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '12px'
};

const metaPillStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '12px',
  backgroundColor: '#f8fafc',
  borderRadius: '12px',
  border: '1px solid #e2e8f0'
};

const metaLabelStyle = {
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontWeight: 700,
  color: '#64748b'
};

const metaValueStyle = {
  fontSize: '14px',
  fontWeight: 700,
  color: '#0f172a',
  marginTop: '3px'
};

const sectionGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px',
  marginBottom: '16px'
};

const cardStyle = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  padding: '18px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
  marginBottom: '16px'
};

const sectionTitleRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '14px'
};

const sectionTitleLeftStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px'
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: '16px',
  color: '#0f172a'
};

const infoRowStyle = {
  display: 'grid',
  gridTemplateColumns: '220px 1fr',
  gap: '12px',
  padding: '10px 0',
  borderBottom: '1px solid #f1f5f9'
};

const infoLabelStyle = {
  fontSize: '12px',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.03em'
};

const infoValueStyle = {
  fontSize: '14px',
  color: '#0f172a',
  fontWeight: 600,
  whiteSpace: 'pre-wrap'
};

const checklistRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  padding: '10px 0',
  borderBottom: '1px solid #f1f5f9'
};

const checklistBadgeStyle = (checked) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '44px',
  height: '24px',
  padding: '0 8px',
  borderRadius: '999px',
  fontSize: '12px',
  fontWeight: 700,
  color: checked ? '#166534' : '#991b1b',
  backgroundColor: checked ? '#dcfce7' : '#fee2e2'
});

const checklistLabelStyle = {
  fontSize: '13px',
  color: '#0f172a',
  lineHeight: 1.5
};

const fileGroupLabelStyle = {
  fontSize: '12px',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  marginBottom: '8px'
};

const fileGridStyle = {
  display: 'grid',
  gap: '8px'
};

const fileCardStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  textDecoration: 'none',
  color: '#0f172a',
  backgroundColor: '#f8fafc'
};

const fileNameStyle = {
  fontSize: '13px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const fileMetaStyle = {
  fontSize: '12px',
  color: '#64748b'
};

const emptyStateStyle = {
  padding: '12px',
  borderRadius: '10px',
  border: '1px dashed #cbd5e1',
  color: '#64748b',
  backgroundColor: '#f8fafc',
  fontSize: '13px'
};

const notFoundCardStyle = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  padding: '18px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)'
};

const responsiveStyles = `
  .inwards-log-detail-page {
    width: 100%;
  }

  @media (max-width: 900px) {
    .inwards-log-detail-page {
      padding: 16px 16px 24px;
    }

    .inwards-log-detail-header,
    .inwards-log-detail-hero-grid,
    .inwards-log-detail-section-grid {
      grid-template-columns: 1fr !important;
    }

    .inwards-log-detail-header {
      display: flex;
      flex-direction: column;
      align-items: stretch;
    }

    .inwards-log-detail-header > div {
      width: 100%;
    }

    .inwards-log-detail-header button,
    .inwards-log-detail-page > button {
      width: 100%;
      justify-content: center;
    }

    .inwards-log-detail-hero,
    .inwards-log-detail-card,
    .inwards-log-detail-notfound {
      padding: 16px;
    }

    .inwards-log-detail-card {
      margin-bottom: 14px;
    }

    .inwards-log-detail-hero-grid {
      display: grid !important;
    }

    .inwards-log-detail-section-grid {
      display: grid !important;
    }

    .inwards-log-detail-card .inwards-log-detail-info-row,
    .inwards-log-detail-card .inwards-log-detail-checklist-row {
      grid-template-columns: 1fr !important;
    }

    .inwards-log-detail-card .inwards-log-detail-file-card {
      align-items: flex-start;
      flex-direction: column;
    }

    .inwards-log-detail-card .inwards-log-detail-file-name {
      white-space: normal;
    }
  }

  @media (max-width: 640px) {
    .inwards-log-detail-page {
      padding: 12px 12px 20px;
    }

    .inwards-log-detail-page h1 {
      font-size: 24px !important;
    }

    .inwards-log-detail-page p {
      font-size: 14px;
    }

    .inwards-log-detail-hero-grid {
      gap: 10px;
    }

    .inwards-log-detail-page .inwards-log-detail-meta-pill {
      padding: 10px;
    }

    .inwards-log-detail-page .inwards-log-detail-info-row {
      padding: 8px 0;
      gap: 6px;
    }
  }
`;

export default InwardsLogDetail;
